import { PhpArray, PhpFloat, normalizeKey, type PhpKeyInput } from './ordered-array';
import { phpFloatToInteger } from '../compile/php-integer-arithmetic';
import { phpJsonNumberString } from '../compile/php-json-number-string';
import { phpRound } from '../pl/php-round';

export const PHP_INT_MAX = 9223372036854775807n;
export const PHP_INT_MIN = -9223372036854775808n;
export type PhpInt = number | bigint;
/** JS has one number type. Safe integral numbers mean PHP int; use PhpFloat for
 * an integral double (1.0, -0.0) when casts/JSON/strict equality depend on its type.
 * All plain objects/JS arrays mean associative PHP arrays at the JSON boundary.
 */
export { PhpFloat } from './ordered-array';
const numeric = /^[\t\n\r\v\f ]*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?[\t\n\r\v\f ]*$/i;
const leading = /^[\t\n\r\v\f ]*([+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?)/i;
const small = (n: bigint): PhpInt => n >= BigInt(Number.MIN_SAFE_INTEGER) && n <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(n) : n;
const arrayLike = (v: unknown): v is object => v !== null && typeof v === 'object' && !(v instanceof PhpFloat);
const entries = (v: object): [PhpKeyInput, unknown][] => v instanceof PhpArray ? [...v] : Object.entries(v);

export function truthy(value: unknown): boolean {
    if (value instanceof PhpFloat) return value.value !== 0;
    if (arrayLike(value)) return value instanceof PhpArray ? value.size !== 0 : Object.keys(value).length !== 0;
    return !(value == null || value === false || value === '' || value === '0' || value === 0 || value === 0n);
}
export const empty = (value: unknown): boolean => !truthy(value);
export function isset(value: unknown, ...path: PhpKeyInput[]): boolean {
    for (const key of path) {
        if (typeof value === 'string') {
            const index = typeof key === 'string' && !/^[\t\n\r\v\f ]*[+-]?\d+[\t\n\r\v\f ]*$/.test(key) ? NaN : Number(key);
            const length = encodeURIComponent(value).replace(/%[\dA-F]{2}/g, '_').length;
            const offset = index < 0 ? length + index : index;
            // isset exposes existence only. A present PHP byte is a one-byte
            // string even when it belongs to a multi-byte UTF-8 code point.
            value = Number.isFinite(offset) && Math.trunc(offset) >= 0 && Math.trunc(offset) < length ? 'x' : undefined;
            continue;
        }
        if (!arrayLike(value)) return false;
        value = value instanceof PhpArray ? value.get(key) : Object.prototype.hasOwnProperty.call(value, String(normalizeKey(key)))
            ? (value as Record<string, unknown>)[String(normalizeKey(key))] : undefined;
    }
    return value !== null && value !== undefined;
}
export function intval(value: unknown): PhpInt {
    if (value instanceof PhpFloat) return small(phpFloatToInteger(value.value));
    if (typeof value === 'bigint') {
        if (value < PHP_INT_MIN || value > PHP_INT_MAX) throw new RangeError('PHP integer outside int64');
        return small(value);
    }
    if (value == null || value === false) return 0;
    if (value === true) return 1;
    if (arrayLike(value)) return Number(truthy(value));
    if (typeof value === 'number') return small(phpFloatToInteger(value));
    const token = String(value).match(leading)?.[1];
    if (!token) return 0;
    if (/^[+-]?\d+$/.test(token)) {
        const n = BigInt(token);
        return small(n > PHP_INT_MAX ? PHP_INT_MAX : n < PHP_INT_MIN ? PHP_INT_MIN : n);
    }
    const n = Number(token);
    if (!Number.isFinite(n)) return 0;
    return small(n >= Number(PHP_INT_MAX) ? PHP_INT_MAX : n <= Number(PHP_INT_MIN) ? PHP_INT_MIN : BigInt(Math.trunc(n)));
}
function toFloat(value: unknown): number {
    if (value instanceof PhpFloat) return value.value;
    if (typeof value === 'number' || typeof value === 'bigint') return value === 0 ? 0 : Number(value);
    if (arrayLike(value)) return Number(truthy(value));
    if (value == null) return 0;
    if (typeof value === 'boolean') return Number(value);
    return Number(String(value).match(leading)?.[1] ?? 0);
}
/** The box preserves PHP's return type even when the result is an integral float.
 * Use .value (or Number(result)) for JS arithmetic; box arithmetic-produced doubles
 * again if their PHP type matters at a cast, strict-comparison or JSON boundary.
 */
export const floatval = (value: unknown): PhpFloat => new PhpFloat(toFloat(value));
export function is_numeric(value: unknown): boolean {
    return value instanceof PhpFloat || typeof value === 'number' || typeof value === 'bigint' || typeof value === 'string' && numeric.test(value);
}
function floatString(n: number): string {
    if (Number.isNaN(n)) return 'NAN';
    if (!Number.isFinite(n)) return n < 0 ? '-INF' : 'INF';
    if (Object.is(n, -0)) return '-0';
    if (!Number.isInteger(n) || Math.abs(n) >= 2 ** 63) return phpJsonNumberString(n);
    // Integral doubles are exactly representable binary integers. Rounding their
    // decimal integer digits avoids Number.toPrecision's binary halfway errors.
    const sign = n < 0 ? '-' : '', raw = BigInt(Math.abs(n)).toString();
    if (raw.length <= 14) return sign + raw;
    const divisor = 10n ** BigInt(raw.length - 14), integer = BigInt(raw);
    let q = integer / divisor, r = integer % divisor;
    if (r * 2n > divisor || r * 2n === divisor && q % 2n !== 0n) q++;
    let exponent = raw.length - 1;
    if (q.toString().length > 14) { q /= 10n; exponent++; }
    const digits = q.toString().replace(/0+$/, '');
    return `${sign}${digits[0]}.${digits.slice(1) || '0'}E+${exponent}`;
}
export function strval(value: unknown): string {
    if (value instanceof PhpFloat) return floatString(value.value);
    if (value == null || value === false) return '';
    if (value === true) return '1';
    if (arrayLike(value)) return 'Array';
    if (typeof value === 'number') return Number.isSafeInteger(value) ? String(value) : floatString(value);
    return String(value);
}
function numericValue(v: unknown): number | bigint {
    if (typeof v === 'bigint') return v;
    if (typeof v === 'string' && /^[\t\n\r\v\f ]*[+-]?\d+[\t\n\r\v\f ]*$/.test(v)) {
        const n = BigInt(v.trim());
        if (n >= PHP_INT_MIN && n <= PHP_INT_MAX) return n;
    }
    return toFloat(v);
}
const sign = (n: number): number => n < 0 ? -1 : n > 0 ? 1 : 0;
function stringCompare(a: string, b: string): number {
    const x = Array.from(a), y = Array.from(b);
    for (let i = 0; i < Math.min(x.length, y.length); i++) {
        const d = x[i]!.codePointAt(0)! - y[i]!.codePointAt(0)!;
        if (d) return sign(d);
    }
    return sign(x.length - y.length);
}
/** PHP comparisons are not a total order (arrays with missing/different keys).
 * Stable array sorting therefore uses Zend's comparison/swap sequence.
 */
export function compare(a: unknown, b: unknown): number {
    if (a === b) return Number.isNaN(a instanceof PhpFloat ? a.value : a) ? 1 : 0;
    if (typeof a === 'boolean' || typeof b === 'boolean') return sign(Number(truthy(a)) - Number(truthy(b)));
    if (a == null || b == null) {
        if (typeof a === 'string' || typeof b === 'string') return stringCompare(strval(a), strval(b));
        return sign(Number(truthy(a)) - Number(truthy(b)));
    }
    if (arrayLike(a) || arrayLike(b)) {
        if (!arrayLike(a)) return -1;
        if (!arrayLike(b)) return 1;
        const aa = entries(a), bb = new PhpArray(entries(b));
        if (aa.length !== bb.size) return sign(aa.length - bb.size);
        for (const [k, v] of aa) {
            if (!bb.has(k)) return 1;
            const d = compare(v, bb.get(k));
            if (d) return d;
        }
        return 0;
    }
    // NaN stays unordered against strings, including nonnumeric ones. Bool,
    // null and array precedence above still use their ordinary PHP rules.
    if (Number.isNaN(a instanceof PhpFloat ? a.value : a) || Number.isNaN(b instanceof PhpFloat ? b.value : b)) return 1;
    if (is_numeric(a) && is_numeric(b)) {
        const x = numericValue(a), y = numericValue(b);
        // PHP promotes int to double if either operand is a double.
        const xx = typeof x === 'bigint' && typeof y === 'bigint' ? x : Number(x);
        const yy = typeof x === 'bigint' && typeof y === 'bigint' ? y : Number(y);
        if (typeof xx === 'number' && Number.isNaN(xx) || typeof yy === 'number' && Number.isNaN(yy)) return 1;
        if (typeof a === 'string' && typeof b === 'string') {
            const overflow = (s: string): number => /^[\t\n\r\v\f ]*[+-]?\d+[\t\n\r\v\f ]*$/.test(s)
                ? BigInt(s.trim()) < PHP_INT_MIN ? -1 : BigInt(s.trim()) > PHP_INT_MAX ? 1 : 0 : 0;
            const ax = overflow(a), by = overflow(b);
            if (typeof x === 'bigint' && by) return -by;
            if (typeof y === 'bigint' && ax) return ax;
            if (xx === yy && (ax !== 0 && ax === by || !Number.isFinite(Number(xx)))) return stringCompare(a, b);
        }
        return xx < yy ? -1 : xx > yy ? 1 : 0;
    }
    return stringCompare(strval(a), strval(b));
}
export const equal = (a: unknown, b: unknown): boolean => compare(a, b) === 0;
export function identical(a: unknown, b: unknown): boolean {
    if (arrayLike(a) && arrayLike(b)) {
        const aa = entries(a), bb = entries(b);
        return aa.length === bb.length && aa.every(([k, v], i) => normalizeKey(k) === normalizeKey(bb[i]![0]) && identical(v, bb[i]![1]));
    }
    const isInt = (v: unknown): boolean => typeof v === 'bigint' || typeof v === 'number' && Number.isSafeInteger(v);
    if (isInt(a) || isInt(b)) return isInt(a) && isInt(b) && BigInt(a as PhpInt) === BigInt(b as PhpInt);
    if (a instanceof PhpFloat || b instanceof PhpFloat) return (a instanceof PhpFloat || typeof a === 'number')
        && (b instanceof PhpFloat || typeof b === 'number') && toFloat(a) === toFloat(b);
    return a === b;
}
function mathNumber(value: unknown): number {
    if (arrayLike(value) || typeof value === 'string' && !is_numeric(value)) throw new TypeError('PHP math requires a number or numeric string');
    // Numeric parameter coercion parses integer syntax as int first; floatval
    // instead casts the text directly to double (and preserves string "-0").
    return typeof value === 'string' ? Number(numericValue(value)) : toFloat(value);
}
function roundNumber(value: unknown, precision = 0): number {
    const n = mathNumber(value), places = Math.max(-2147483648, Math.min(2147483647, Math.trunc(precision)));
    if (!Number.isFinite(n) || n === 0) return n;
    if (places > 308) return n;
    if (places < -308) return 0;
    if (places >= 0 && places < 23) {
        const result = phpRound(n, places);
        return result === 0 && n < 0 ? -0 : result;
    }
    const factor = 10 ** Math.abs(places), direction = n < 0 ? -1 : 1;
    let whole = Math.trunc(places > 0 ? n * factor : n / factor);
    if ((places > 0 ? (whole + direction) / factor : (whole + direction) * factor) === n) whole += direction;
    if (Math.abs(whole) >= 1e16) return n;
    if (Math.abs(n) >= Math.abs(places > 0 ? (whole + direction * 0.5) / factor : (whole + direction * 0.5) * factor)) whole += direction;
    // PHP uses a decimal-string rescale beyond its exact powers-of-ten table.
    // That path also serializes a rounded negative zero as positive zero.
    const result = Math.abs(places) > 22 ? Number(`${whole}e${-places}`) : whole * factor;
    return Number.isFinite(result) ? result : n;
}
export const round = (value: unknown, precision = 0): PhpFloat => new PhpFloat(roundNumber(value, precision));
function integerArgument(value: unknown): bigint {
    if (typeof value === 'bigint') return BigInt(intval(value));
    if (typeof value === 'string' && is_numeric(value)) {
        const numeric = numericValue(value);
        if (typeof numeric === 'bigint') return numeric;
    }
    const n = mathNumber(value);
    if (!Number.isFinite(n) || n >= 2 ** 63 || n < -(2 ** 63)) throw new TypeError('PHP integer argument outside int64');
    return BigInt(intval(value));
}
function mathError(name: string, message: string): never {
    const error = new RangeError(message); error.name = name; throw error;
}
export function intdiv(a: unknown, b: unknown): PhpInt {
    const x = integerArgument(a), y = integerArgument(b);
    if (y === 0n) mathError('DivisionByZeroError', 'Division by zero');
    if (x === PHP_INT_MIN && y === -1n) mathError('ArithmeticError', 'Division of PHP_INT_MIN by -1');
    return small(x / y);
}
export function mod(a: unknown, b: unknown): PhpInt {
    if (arrayLike(a) || arrayLike(b) || typeof a === 'string' && !a.match(leading) || typeof b === 'string' && !b.match(leading)) throw new TypeError('Unsupported PHP modulo operand');
    const divisor = BigInt(intval(b));
    if (divisor === 0n) mathError('DivisionByZeroError', 'Modulo by zero');
    return small(BigInt(intval(a)) % divisor);
}
export const floor = (value: unknown): PhpFloat => new PhpFloat(Math.floor(mathNumber(value)));
export const ceil = (value: unknown): PhpFloat => new PhpFloat(Math.ceil(mathNumber(value)));
export function abs(value: unknown): PhpInt | PhpFloat {
    const n = typeof value === 'string' && is_numeric(value) ? numericValue(value) : value;
    if (typeof n === 'bigint') return n === PHP_INT_MIN ? new PhpFloat(Number(-n)) : small(n < 0 ? -n : n);
    if (typeof value === 'string' && typeof n === 'number') return new PhpFloat(Math.abs(n));
    if (typeof n === 'number' && Number.isSafeInteger(n) || typeof n === 'boolean' || n == null) return Math.abs(mathNumber(n));
    return new PhpFloat(Math.abs(mathNumber(n)));
}
function extreme(args: unknown[], direction: number): unknown {
    if (!args.length) mathError('ArgumentCountError', 'PHP max/min requires an argument');
    const single = args.length === 1;
    if (single && !arrayLike(args[0])) throw new TypeError('PHP max/min single argument must be an array');
    const values = single ? entries(args[0] as object).map(entry => entry[1]) : args;
    if (!values.length) mathError('ValueError', 'PHP max/min requires a nonempty array');
    // PHP 8.4 frameless direct two-argument calls compare lhs to rhs, with
    // numeric fast paths and asymmetric ties (min chooses rhs, max lhs).
    const integer = (v: unknown): v is PhpInt => typeof v === 'bigint' || typeof v === 'number' && Number.isSafeInteger(v);
    const nativeNumber = (v: unknown): boolean => integer(v) || v instanceof PhpFloat || typeof v === 'number';
    const exactInt = (v: unknown): boolean => !integer(v) || BigInt(Number(v)) === BigInt(v);
    const intCompare = (a: PhpInt, b: PhpInt): number => BigInt(a) < BigInt(b) ? -1 : BigInt(a) > BigInt(b) ? 1 : 0;
    if (!single && values.length === 2) {
        const [a, b] = values;
        const bothNumbers = nativeNumber(a) && nativeNumber(b);
        let chooseLeft: boolean;
        if (integer(a) && integer(b)) chooseLeft = direction > 0 ? intCompare(a, b) >= 0 : intCompare(a, b) < 0;
        else if (bothNumbers && exactInt(a) && exactInt(b)) chooseLeft = direction > 0 ? Number(a) >= Number(b) : Number(a) < Number(b);
        else chooseLeft = direction > 0 ? compare(a, b) >= 0 : compare(a, b) < 0;
        return phpCopy(chooseLeft ? a : b);
    }
    let best = values[0], mode: 'int' | 'float' | 'generic' = single || !nativeNumber(best) ? 'generic' : integer(best) ? 'int' : 'float';
    for (const value of values.slice(1)) {
        if (mode !== 'generic') {
            if (!nativeNumber(value)) mode = 'generic';
            else if (mode === 'int' && !integer(value)) mode = exactInt(best) ? 'float' : 'generic';
            else if (mode === 'float' && !exactInt(value)) mode = 'generic';
        }
        const replace = single ? compare(best, value) * direction < 0
            : mode === 'int' ? intCompare(best as PhpInt, value as PhpInt) * direction < 0
                : mode === 'float' ? (direction > 0 ? Number(best) < Number(value) : Number(best) > Number(value))
                : compare(value, best) * direction > 0;
        if (replace) best = value;
    }
    return phpCopy(best);
}
export const max = (...values: unknown[]): unknown => extreme(values, 1);
export const min = (...values: unknown[]): unknown => extreme(values, -1);

/** Deep value copy for the port's JSON/PhpArray domain, including objects; never
 * aliases nested mutable storage. Native PHP class-object handles are outside this
 * domain. This explicit boundary is stricter than PHP's class-object assignment.
 */
export function phpCopy<T>(value: T): T {
    if (value instanceof PhpFloat) return new PhpFloat(value.value) as T;
    if (value instanceof PhpArray) return value.copy(phpCopy) as T;
    if (Array.isArray(value)) return value.map(phpCopy) as T;
    if (value !== null && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, phpCopy(v)])) as T;
    return value;
}
